import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faCalendar,
  faClock,
  faMapMarkerAlt,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { MapContainer, TileLayer, Marker, Popup as MapPopup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { motion, AnimatePresence } from "framer-motion";
import CheckoutConfirmModal from "./CheckoutConfirmModal";
import { formatDateVN } from "../../../utils/formatUtils";
import { DEFAULT_MAP_CENTER, hasValidGps, parseLatLng } from "./attendanceLocationUtils";

// 🎯 Định nghĩa icon marker
const greenIcon = new L.Icon({
  iconUrl: "https://maps.gstatic.com/mapfiles/ms2/micons/green-dot.png",
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});
const redIcon = new L.Icon({
  iconUrl: "https://maps.gstatic.com/mapfiles/ms2/micons/red-dot.png",
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});

const ActiveCheckinList = ({ data = [], currentUserId, currentUserRole, onCheckout, onUpdated }) => {
  const [selectedLog, setSelectedLog] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedCheckoutLog, setSelectedCheckoutLog] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  const ActiveCheckinCard = ({ log }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [workedTime, setWorkedTime] = useState("");

    const today = new Date().toLocaleDateString("en-CA");
    const workDateStr = new Date(log.work_date).toLocaleDateString("en-CA");
    const canCheckout = log.employee_id === currentUserId && workDateStr === today;

    // 🕒 Tính thời gian làm việc realtime
    useEffect(() => {
      if (!log.check_in_time) return;

      const updateWorkedTime = () => {
        const [h, m, s] = log.check_in_time.split(":").map(Number);
        const checkinDate = new Date(log.work_date);
        checkinDate.setHours(h, m, s, 0);

        const now = new Date();
        const diffMs = now - checkinDate;
        if (diffMs < 0) return setWorkedTime("0 phút");

        const diffHours = Math.floor(diffMs / 3600000);
        const diffMinutes = Math.floor((diffMs % 3600000) / 60000);

        if (diffHours > 0) setWorkedTime(`${diffHours}h ${diffMinutes}m`);
        else setWorkedTime(`${diffMinutes} phút`);
      };

      updateWorkedTime();
      const timer = setInterval(updateWorkedTime, 60000);
      return () => clearInterval(timer);
    }, [log.check_in_time, log.work_date]);

    const getWorkedTagColor = () => {
      if (!workedTime) return "bg-gray-100 text-gray-600";
      const h = parseInt(workedTime);
      if (isNaN(h)) return "bg-gray-100 text-gray-600";
      if (h < 4) return "bg-yellow-100 text-yellow-700";
      if (h < 8) return "bg-green-100 text-green-700";
      return "bg-blue-100 text-blue-700";
    };

    const formatBranchName = (str) => {
      if (!str) return "";
      return str.trim().replace(/\s+/g, " ").toUpperCase();
    };

    return (
      <div className="relative bg-white border border-gray-200 rounded-xl shadow-sm p-4 w-full md:w-64 transition hover:shadow-md">
        {/* 🏷 Tag thời gian làm việc */}
        {workedTime && (
          <div
            className={`absolute top-2 right-2 px-3 py-1 text-xs font-semibold rounded-full ${getWorkedTagColor()}`}
          >
            ⏱ {workedTime}
          </div>
        )}

        {/* 👤 Thông tin nhân viên */}
        <div className="flex items-center text-base font-semibold text-red-700 mb-2">
          <FontAwesomeIcon icon={faUser} className="mr-2 text-gray-400" />
          {log.employee_name.toUpperCase()}
        </div>

        {/* 🗺 Thông tin chi tiết */}
        <div className="text-sm text-gray-600 space-y-1">
          <div>
            <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-gray-400" />
            <span className="font-semibold">
              {formatBranchName(log.branch_name)}
            </span>
          </div>
          <div>
            <FontAwesomeIcon icon={faCalendar} className="mr-2 text-gray-400" />
            {formatDateVN(log.work_date)}
          </div>
          <div>
            <FontAwesomeIcon icon={faClock} className="mr-2 text-gray-400" />
            {log.check_in_time}
          </div>

          {/* 📍 Nút xem vị trí */}
          {(hasValidGps(log.check_in_location) || hasValidGps(log.check_out_location)) && (
            <div className="pt-1">
              <button
                onClick={() => setSelectedLog(log)}
                className="text-blue-600 text-sm font-medium hover:underline"
              >
                📍 Xem vị trí
              </button>
            </div>
          )}
        </div>
      
        {/* 🔘 Nút Checkout */}
        {canCheckout && (
          <>
            <button
              onClick={() => {
                setSelectedCheckoutLog(log);
                setShowCheckoutModal(true);
              }}
              disabled={loadingId === log.id}
              className={`mt-4 w-full text-white py-2 px-4 rounded-lg font-medium transition ${
                loadingId === log.id
                  ? "bg-gray-400 cursor-wait"
                  : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {loadingId === log.id ? "Đang xử lý..." : "Checkout"}
            </button>

            {/* Popup xác nhận Checkout */}
            {showCheckoutModal && selectedCheckoutLog?.id === log.id && (
              <CheckoutConfirmModal
                isOpen={showCheckoutModal}
                checkInTime={selectedCheckoutLog?.check_in_time}
                workDate={selectedCheckoutLog?.work_date}
                onClose={() => setShowCheckoutModal(false)}
                onConfirm={async (data) => {
                  try {
                    setLoadingId(log.id);
                    await onCheckout(selectedCheckoutLog, data);
                    onUpdated?.();
                  } finally {
                    setLoadingId(null);
                    setShowCheckoutModal(false);
                  }
                }}
              />
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>      
      <div className="mb-2">   
        <div className="flex flex-wrap gap-4">
          {data.map((log) => (
            <ActiveCheckinCard key={log.id} log={log} />
          ))}
        </div>
      </div>

      {/* 🗺 Popup xem vị trí */}
      <AnimatePresence>
        {selectedLog && (
          <motion.div
            key="overlay"
            className="fixed inset-0 backdrop-blur-sm bg-black bg-opacity-40 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              key="popup"
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="bg-white w-[700px] max-w-[95vw] rounded-2xl shadow-2xl overflow-hidden relative"
            >
              <div className="flex justify-between items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white">
                <h3 className="text-lg font-semibold">Vị trí chấm công</h3>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="text-white hover:text-gray-200"
                >
                  <FontAwesomeIcon icon={faXmark} size="lg" />
                </button>
              </div>

              {/* 🗺 Bản đồ Leaflet */}
              <div className="h-[300px] w-full">
                {(() => {
                  const inLoc = parseLatLng(selectedLog.check_in_location);
                  const outLoc = parseLatLng(selectedLog.check_out_location);
                  const hasGps = Boolean(inLoc || outLoc);

                  if (!hasGps) {
                    return (
                      <div className="flex h-full items-center justify-center text-gray-500">
                        Không có tọa độ GPS hợp lệ.
                      </div>
                    );
                  }

                  const center = inLoc || outLoc || DEFAULT_MAP_CENTER;
                  const linePoints = inLoc && outLoc ? [inLoc, outLoc] : null;

                  return (
                    <MapContainer
                      key={selectedLog.id}
                      center={center}
                      zoom={14}
                      scrollWheelZoom={true}
                      className="h-full w-full z-0"
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution="© OpenStreetMap contributors"
                      />
                      {inLoc && (
                        <Marker position={inLoc} icon={greenIcon}>
                          <MapPopup>Vị trí Check-in</MapPopup>
                        </Marker>
                      )}
                      {outLoc && (
                        <Marker position={outLoc} icon={redIcon}>
                          <MapPopup>Vị trí Check-out</MapPopup>
                        </Marker>
                      )}
                      {linePoints && <Polyline positions={linePoints} color="blue" />}
                    </MapContainer>
                  );
                })()}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ActiveCheckinList;
